import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Pencil } from "lucide-react";
import { Card } from "../../../components/Card";
import { SectionTitle } from "../../../components/SectionTitle";
import { Button } from "../../../components/Button";
import { InfoBox } from "../../../components/InfoBox";
import { LoadingContainer } from "../../../components/LoadingContainer";
import { ManagePaymentTerminalsModal } from "./ManagePaymentTerminalsModal";
import { QuantityBillingItemEditModal } from "./QuantityBillingItemEditModal";
import { BillingItemMonthlyPrice } from "./BillingItemMonthlyPrice";
import {
  useGetEntitlementsQuery,
  useGetMeteredUsageQuery,
} from "../../../query/entitlementsQuery";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import { formatMoney } from "../../../utils/formatMoney";
import { formatDate, toDateString } from "../../../utils/dateUtils";
import { BillingItemType } from "@KassaroAB/shared/types/enums/BillingItemType";
import { BillingPeriod } from "@KassaroAB/shared/types/enums/BillingPeriod";
import { Currency } from "@KassaroAB/shared/types/enums/currency";
import { PaymentProvider } from "@KassaroAB/shared/types/enums/PaymentProvider";
import type { RestBillingItem } from "@KassaroAB/shared/types/billingItem";
import type { RestAvailableBillingItem } from "@KassaroAB/shared/types/availableBillingItem";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import {
  getCurrentOrNextPositiveSegment,
  getQuantityOnDate,
  getQuantityTimeline,
  isBillingItemCurrentOrFuture,
} from "../../../utils/billingItemSegments";

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.x2}px;
  margin-bottom: ${spacing.x2}px;
  flex-wrap: wrap;
`;

const TerminalList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const TerminalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.x2}px;
  width: 100%;
  padding: ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  background-color: ${colors.background};
`;

const TerminalInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
  min-width: 0;
`;

const TerminalName = styled.div`
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.text};
`;

const TerminalTotal = styled.div`
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.text};
`;

const TerminalMeta = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const TerminalBreakdown = styled.div<{ $overage?: boolean }>`
  font-size: ${fontSizes.small}px;
  font-weight: 500;
  color: ${({ $overage }) =>
    $overage ? colors.warningText : colors.textMuted};
`;

const TerminalActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.x1}px;
  flex-shrink: 0;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${colors.textMuted};
  cursor: pointer;
  padding: ${spacing.x05}px;
  border-radius: 4px;

  &:hover {
    background-color: ${colors.backgroundDark};
    color: ${colors.text};
  }
`;

const EmptyText = styled.p`
  margin: 0;
  color: ${colors.textMuted};
`;

const Badge = styled.span`
  font-size: ${fontSizes.small}px;
  font-weight: 500;
  padding: ${spacing.x05}px ${spacing.x1}px;
  margin-left: ${spacing.x1}px;
  border-radius: 4px;
  background-color: ${colors.infoBackground};
  color: ${colors.info};
`;

const BreakdownItem = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const BreakdownValue = styled.span`
  color: ${colors.text};
  font-weight: 500;
  white-space: nowrap;
`;

interface PaymentTerminalsSectionProps {
  billingItems: RestBillingItem[] | undefined;
  availableBillingItems: RestAvailableBillingItem[] | undefined;
  isLoading?: boolean;
  billingPeriod: BillingPeriod;
  startDate: string;
}

const getGrantedProviders = (
  availableItem: RestAvailableBillingItem | undefined
): PaymentProvider[] => {
  const paymentTerminals = availableItem?.grants?.paymentTerminals ?? {};
  return (Object.keys(paymentTerminals) as PaymentProvider[]).filter(
    (provider) => (paymentTerminals[provider] ?? 0) > 0
  );
};

const getLicensedTerminalCount = (
  availableItem: RestAvailableBillingItem | undefined,
  quantity: number,
  entitlementsCount?: number
): number => {
  const providers = getGrantedProviders(availableItem);
  if (providers.length > 0) {
    return providers.reduce((total, provider) => {
      const grantPerUnit =
        availableItem?.grants?.paymentTerminals?.[provider] ?? 0;
      return total + grantPerUnit * quantity;
    }, 0);
  }
  if (entitlementsCount !== undefined) {
    return entitlementsCount;
  }
  return quantity;
};

export const PaymentTerminalsSection = ({
  billingItems,
  availableBillingItems,
  isLoading = false,
  billingPeriod,
  startDate,
}: PaymentTerminalsSectionProps) => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const today = toDateString(new Date());
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [quantityItem, setQuantityItem] = useState<RestBillingItem | null>(
    null
  );

  const { data: entitlements } = useGetEntitlementsQuery(storeId ?? null);
  const { data: meteredUsage } = useGetMeteredUsageQuery(storeId ?? null);

  const lookup = new Map(
    (availableBillingItems || []).map((item) => [item.id, item])
  );
  const displayedTerminalItems = (billingItems ?? [])
    .filter(
      (billingItem) =>
        billingItem.type === BillingItemType.PAYMENT_METHOD &&
        isBillingItemCurrentOrFuture(billingItem, today)
    )
    .sort((left, right) => left.startDate.localeCompare(right.startDate));
  const quantityAvailable = quantityItem
    ? lookup.get(quantityItem.availableBillingItemId)
    : undefined;

  const meteredByProvider = new Map(
    (meteredUsage?.providers ?? []).map((provider) => [
      provider.paymentProvider,
      provider,
    ])
  );
  const hasOverage = (meteredUsage?.providers ?? []).some(
    (provider) => provider.overageDevices > 0
  );

  const openQuantityEditor = (billingItem: RestBillingItem) => {
    setQuantityItem(billingItem);
  };

  const closeQuantityEditor = () => {
    setQuantityItem(null);
  };

  return (
    <Card>
      <SectionHeader>
        <SectionTitle>{t("packageSelection.paymentTerminals")}</SectionTitle>
        <Button onClick={() => setIsAddOpen(true)}>
          {t("packageSelection.addTerminals")}
        </Button>
      </SectionHeader>

      {hasOverage && (
        <InfoBox variant="warning">
          {t("packageSelection.meteredOverageWarning")}
        </InfoBox>
      )}

      {isLoading ? (
        <LoadingContainer message={t("packageSelection.loading")} />
      ) : displayedTerminalItems.length === 0 ? (
        <EmptyText>{t("packageSelection.noPaymentTerminals")}</EmptyText>
      ) : (
        <TerminalList>
          {displayedTerminalItems.map((billingItem) => {
            const availableItem = lookup.get(
              billingItem.availableBillingItemId
            );
            const displaySegment = getCurrentOrNextPositiveSegment(
              billingItem,
              today
            );
            const isUpcoming =
              (displaySegment?.startDate ?? billingItem.startDate) > today;
            const quantity =
              displaySegment?.quantity ??
              getQuantityOnDate(
                [billingItem],
                billingItem.availableBillingItemId,
                today
              );
            const timeline = getQuantityTimeline(
              billingItems ?? [],
              billingItem.availableBillingItemId
            );
            const monthlyPrice =
              availableItem?.pricing?.monthlyPrice?.currencies?.[Currency.SEK];
            const providers = getGrantedProviders(availableItem);
            const licensedCount = getLicensedTerminalCount(
              availableItem,
              quantity,
              isUpcoming
                ? undefined
                : providers.reduce(
                    (total, provider) =>
                      total +
                      (entitlements?.paymentTerminals?.[provider] ?? 0),
                    0
                  )
            );
            const primaryProvider = providers[0];
            const metered = primaryProvider
              ? meteredByProvider.get(primaryProvider)
              : undefined;
            const activeCount = isUpcoming ? undefined : metered?.devices;
            const overageCount = metered?.overageDevices ?? 0;
            const unusedCount =
              activeCount === undefined
                ? undefined
                : Math.max(0, licensedCount - activeCount);
            const displayStartDate =
              displaySegment?.startDate ?? billingItem.startDate;
            const displayEndDate =
              displaySegment?.endDate ?? billingItem.endDate;

            return (
              <TerminalRow key={billingItem.id}>
                <TerminalInfo>
                  <TerminalName>
                    {availableItem?.name ?? billingItem.availableBillingItemId}
                    {isUpcoming && (
                      <Badge>{t("billingItems.status.upcoming")}</Badge>
                    )}
                  </TerminalName>
                  <TerminalTotal>
                    {t("packageSelection.totalTerminals", {
                      count: licensedCount,
                    })}
                  </TerminalTotal>
                  {activeCount !== undefined && (
                    <TerminalBreakdown $overage={overageCount > 0}>
                      {overageCount > 0
                        ? t("packageSelection.terminalsActiveOverage", {
                            active: activeCount,
                            overage: overageCount,
                          })
                        : t("packageSelection.terminalsActiveUnused", {
                            active: activeCount,
                            unused: unusedCount ?? 0,
                          })}
                    </TerminalBreakdown>
                  )}
                  {timeline
                    .filter(
                      (entry) =>
                        entry.startDate >
                        (isUpcoming ? displayStartDate : today)
                    )
                    .map((entry) => (
                      <TerminalMeta key={entry.startDate}>
                        {t("packageSelection.scheduledTerminalChange", {
                          count: getLicensedTerminalCount(
                            availableItem,
                            entry.quantity
                          ),
                          date: formatDate(entry.startDate),
                        })}
                      </TerminalMeta>
                    ))}
                  <TerminalMeta>
                    {t(
                      `billingItems.billingPeriod.${
                        billingItem.billingPeriod ?? BillingPeriod.MONTHLY
                      }`
                    )}
                    {" · "}
                    {formatDate(displayStartDate)}
                    {" – "}
                    {displayEndDate
                      ? formatDate(displayEndDate)
                      : t("entitlements.ongoing")}
                  </TerminalMeta>
                </TerminalInfo>
                <TerminalActions>
                  <BillingItemMonthlyPrice
                    unitPrice={monthlyPrice}
                    quantity={quantity}
                    discount={billingItem.discount}
                  />
                  <IconButton
                    type="button"
                    aria-label={t("packageSelection.editDetails")}
                    onClick={() => openQuantityEditor(billingItem)}
                  >
                    <Pencil size={16} />
                  </IconButton>
                </TerminalActions>
              </TerminalRow>
            );
          })}
        </TerminalList>
      )}

      <ManagePaymentTerminalsModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        billingItems={billingItems}
        defaultBillingPeriod={billingPeriod}
        defaultStartDate={startDate}
      />

      {quantityItem && (
        <QuantityBillingItemEditModal
          isOpen
          onClose={closeQuantityEditor}
          title={
            quantityAvailable?.name ?? quantityItem.availableBillingItemId
          }
          billingItems={billingItems}
          billingItem={quantityItem}
          availableBillingItemId={quantityItem.availableBillingItemId}
          quantityLabel={t("packageSelection.terminalCount")}
          defaultEffectiveFrom={startDate}
          previewTitle={t("packageSelection.deviceChangePreview")}
          formatTimelineQuantity={(quantity) =>
            getLicensedTerminalCount(quantityAvailable, quantity)
          }
          renderPreview={(billingQuantity) => {
            const licensedCount = getLicensedTerminalCount(
              quantityAvailable,
              billingQuantity
            );
            const monthlyPrice =
              quantityAvailable?.pricing?.monthlyPrice?.currencies?.[
                Currency.SEK
              ];
            const monthlyTotal = monthlyPrice
              ? {
                  amount: Math.max(
                    0,
                    monthlyPrice.amount * billingQuantity -
                      (quantityItem.discount?.amount ?? 0)
                  ),
                  currency: monthlyPrice.currency,
                }
              : null;
            return (
              <BreakdownItem>
                <span>
                  {t("packageSelection.totalTerminals", {
                    count: licensedCount,
                  })}
                </span>
                <BreakdownValue>
                  {monthlyPrice && monthlyTotal && billingQuantity > 0
                    ? t("packageSelection.extraDevicesCost", {
                        price: formatMoney(monthlyPrice),
                        total: formatMoney(monthlyTotal),
                      })
                    : t("packageSelection.noExtraDevicesCost")}
                </BreakdownValue>
              </BreakdownItem>
            );
          }}
        />
      )}
    </Card>
  );
};
